/** Source observations are independent of engraving, quantization and undo. */
export type PerformanceNote = {
  id: string;
  midi: number;
  channel: number;
  velocity: number | null;
  startedAtMs: number;
  endedAtMs: number;
  interrupted?: boolean;
  /**
   * Recognition certainty for acoustic observations, 0..1. MIDI notes leave this
   * undefined: the instrument reported the pitch, so there is nothing to review.
   */
  confidence?: number;
};

export type PerformanceController = {
  channel: number;
  controller: number;
  value: number;
  atMs: number;
};

/**
 * Locator for the retained original recording. The bytes live in a separate local
 * asset store, never inside the synced document, so opening a composition on another
 * device restores the score without pretending the audio travelled with it.
 */
export type PerformanceTakeAudio = {
  assetId: string;
  mimeType: string;
  byteLength: number;
  durationMs: number;
  sampleRate?: number;
};

export type PerformanceTake = {
  id: string;
  name: string;
  source: 'midi' | 'audio';
  createdAt: string;
  durationMs: number;
  startDivisions: number;
  tempo: number;
  notes: PerformanceNote[];
  controllers: PerformanceController[];
  status?: 'recording' | 'complete' | 'interrupted';
  audio?: PerformanceTakeAudio;
};

/** Below this the detector was unsure enough that a human should look at the note. */
export const TAKE_REVIEW_CONFIDENCE_THRESHOLD = 0.55;

export const isTakeNoteUncertain = (note: PerformanceNote) =>
  typeof note.confidence === 'number' && note.confidence < TAKE_REVIEW_CONFIDENCE_THRESHOLD;

export const countUncertainTakeNotes = (take: PerformanceTake) =>
  take.notes.filter(isTakeNoteUncertain).length;

const isTakeAudio = (value: unknown): value is PerformanceTakeAudio => {
  const audio = value as PerformanceTakeAudio | null;
  return Boolean(
    audio &&
    typeof audio.assetId === 'string' &&
    audio.assetId &&
    typeof audio.mimeType === 'string' &&
    audio.mimeType &&
    Number.isFinite(audio.byteLength) &&
    audio.byteLength >= 0 &&
    Number.isFinite(audio.durationMs) &&
    audio.durationMs >= 0 &&
    (audio.sampleRate === undefined || (Number.isFinite(audio.sampleRate) && audio.sampleRate > 0)),
  );
};

export function isPerformanceTake(value: unknown): value is PerformanceTake {
  const take = value as PerformanceTake | null;
  if (
    !take ||
    typeof take.id !== 'string' ||
    !take.id ||
    typeof take.name !== 'string' ||
    !['midi', 'audio'].includes(take.source) ||
    typeof take.createdAt !== 'string' ||
    !Number.isFinite(Date.parse(take.createdAt)) ||
    !Number.isFinite(take.durationMs) ||
    take.durationMs < 0 ||
    !Number.isFinite(take.startDivisions) ||
    take.startDivisions < 0 ||
    !Number.isFinite(take.tempo) ||
    take.tempo <= 0 ||
    !Array.isArray(take.notes) ||
    !Array.isArray(take.controllers) ||
    (take.status !== undefined &&
      !['recording', 'complete', 'interrupted'].includes(take.status)) ||
    (take.audio !== undefined && !isTakeAudio(take.audio))
  )
    return false;
  const ids = new Set<string>();
  const inRange = (number: number, maximum: number) =>
    Number.isInteger(number) && number >= 0 && number <= maximum;
  for (const note of take.notes) {
    if (
      !note ||
      typeof note.id !== 'string' ||
      !note.id ||
      ids.has(note.id) ||
      !inRange(note.midi, 127) ||
      !inRange(note.channel, 16) ||
      (note.velocity !== null && !inRange(note.velocity, 127)) ||
      !Number.isFinite(note.startedAtMs) ||
      note.startedAtMs < 0 ||
      !Number.isFinite(note.endedAtMs) ||
      note.endedAtMs < note.startedAtMs ||
      note.endedAtMs > take.durationMs ||
      (note.confidence !== undefined &&
        (!Number.isFinite(note.confidence) || note.confidence < 0 || note.confidence > 1))
    )
      return false;
    ids.add(note.id);
  }
  return take.controllers.every(
    (event) =>
      event &&
      inRange(event.channel, 16) &&
      inRange(event.controller, 127) &&
      inRange(event.value, 127) &&
      Number.isFinite(event.atMs) &&
      event.atMs >= 0 &&
      event.atMs <= take.durationMs,
  );
}

export const clonePerformanceTake = (take: PerformanceTake): PerformanceTake => ({
  ...take,
  notes: take.notes.map((note) => ({ ...note })),
  controllers: take.controllers.map((event) => ({ ...event })),
  ...(take.audio ? { audio: { ...take.audio } } : {}),
});

const midiValue = (value: number) => Math.max(0, Math.min(127, Math.round(value)));

/** One take-relative clock; each channel/pitch has its own release and velocity. */
export class PerformanceRecorder {
  private readonly notes: PerformanceNote[] = [];
  private readonly controllers: PerformanceController[] = [];
  private readonly held = new Map<string, PerformanceNote>();
  private finished = false;
  private lastObservedAt = 0;
  private audio?: PerformanceTakeAudio;

  constructor(
    readonly id: string,
    private readonly startedAt: number,
    private readonly metadata: Pick<
      PerformanceTake,
      'source' | 'startDivisions' | 'tempo' | 'name'
    >,
    private readonly createdAt = new Date().toISOString(),
  ) {}

  noteOn(
    midi: number,
    velocity: number | null,
    channel: number,
    now: number,
    confidence?: number,
  ): string {
    if (this.finished) throw new Error('The take has already ended.');
    this.lastObservedAt = now;
    const key = `${channel}:${midi}`;
    this.noteOff(midi, channel, now);
    const note: PerformanceNote = {
      id: `${this.id}:note:${this.notes.length + 1}`,
      midi: midiValue(midi),
      channel,
      velocity: velocity === null ? null : midiValue(velocity),
      startedAtMs: Math.max(0, now - this.startedAt),
      endedAtMs: Math.max(0, now - this.startedAt),
      ...(Number.isFinite(confidence)
        ? { confidence: Math.max(0, Math.min(1, confidence as number)) }
        : {}),
    };
    this.notes.push(note);
    this.held.set(key, note);
    return note.id;
  }

  /** The retained recording is attached once the encoder reports its final bytes. */
  attachAudio(audio: PerformanceTakeAudio) {
    this.audio = { ...audio };
  }

  noteOff(midi: number, channel: number, now: number) {
    this.lastObservedAt = now;
    const key = `${channel}:${midi}`;
    const note = this.held.get(key);
    if (!note) return;
    note.endedAtMs = Math.max(note.startedAtMs, now - this.startedAt);
    this.held.delete(key);
  }

  controller(channel: number, controller: number, value: number, now: number) {
    if (this.finished) return;
    this.lastObservedAt = now;
    this.controllers.push({
      channel,
      controller,
      value: midiValue(value),
      atMs: Math.max(0, now - this.startedAt),
    });
  }

  /** Engraved chords can link to several independently captured observations. */
  notesNear(midiNotes: number[], now: number, toleranceMs = 80) {
    const atMs = now - this.startedAt;
    return this.notes
      .filter(
        (note) => midiNotes.includes(note.midi) && Math.abs(note.startedAtMs - atMs) <= toleranceMs,
      )
      .map((note) => ({ ...note }));
  }

  snapshot(): PerformanceTake {
    const held = new Set(this.held.values());
    return {
      ...this.metadata,
      id: this.id,
      createdAt: this.createdAt,
      durationMs: Math.max(0, this.lastObservedAt - this.startedAt),
      status: this.finished ? 'complete' : 'recording',
      notes: this.notes.map((note) =>
        held.has(note)
          ? {
              ...note,
              endedAtMs: Math.max(note.startedAtMs, this.lastObservedAt - this.startedAt),
              interrupted: true,
            }
          : { ...note },
      ),
      controllers: this.controllers.map((event) => ({ ...event })),
      ...(this.audio ? { audio: { ...this.audio } } : {}),
    };
  }

  finish(now: number): PerformanceTake {
    this.lastObservedAt = now;
    for (const note of this.held.values()) {
      note.endedAtMs = Math.max(note.startedAtMs, now - this.startedAt);
      note.interrupted = true;
    }
    this.held.clear();
    this.finished = true;
    return this.snapshot();
  }
}
